import {
  AbonnementCancelUpdate,
  Profile,
  ProfileFilter,
  ProfileUpdate,
  UserUpdate,
} from "@happy-legal/graphql-types";
import { graphQlClient } from "../clients";
import {
  MUTATION_CANCEL_ABO,
  MUTATION_CHANGE_USER,
  MUTATION_REVOKE_ABO,
  MUTATION_UPDATE_PROFILE,
} from "../gql/mutations";
import { MUTATION_UPDATE_USER } from "../gql/mutations/user";
import { QUERY_GET_ALL_PROFILES, QUERY_GET_PROFILE } from "../gql/queries";
import { handleCatchReducer } from "./handleCatchReducer";
import { notify } from "../utils/notify";

export const GET_ALL_PROFILES = "GET_ALL_PROFILES";
type TGET_ALL_PROFILES = {
  type: "GET_ALL_PROFILES";
  filter?: ProfileFilter;
  notEqual?: ProfileUpdate;
  limit?: number;
  offset?: number;
  sortBy?: Record<string, string>; // SortBy;
};

export const GET_PROFILE = "GET_PROFILE";
type TGET_PROFILE = {
  type: "GET_PROFILE";
  id: string;
};

export const UPDATE_PROFILE = "UPDATE_PROFILE";
export type TUPDATE_PROFILE = {
  type: "UPDATE_PROFILE";
  id: string;
  payload: ProfileUpdate;
};

export const UPDATE_USER = "UPDATE_USER";
type TUPDATE_USER = {
  type: "UPDATE_USER";
  id: string;
  payload: UserUpdate;
};

export const CANCEL_ABO = "CANCEL_ABO";
type TCANCEL_ABO = {
  type: "CANCEL_ABO";
  id: string;
  // FIXME: Omitted because backend type is incorrect
  cancelUpdate?: Omit<AbonnementCancelUpdate, "cancellation">; // cancellation = now
};

export const REVOKE_ABO = "REVOKE_ABO";
type TREVOKE_ABO = {
  type: "REVOKE_ABO";
  aboId?: string;
  profileId: string;
  refundTransactions?: boolean; // = true
  revokeDate?: string; // = now
};

export const CHANGE_USER = "CHANGE_USER";
export type TCHANGE_USER = {
  type: "CHANGE_USER";
  oldUserId: string;
  newUserId: string;
  deleteOldUser?: boolean; // = false
};

export type TProfileAction =
  | TGET_ALL_PROFILES
  | TGET_PROFILE
  | TUPDATE_PROFILE
  | TUPDATE_USER
  | TCANCEL_ABO
  | TREVOKE_ABO
  | TCHANGE_USER;

export type TProfileState = {
  profile: Profile;
  allProfiles: {
    items: Profile[];
    total: number;
  };
};

export const profileReducer = async (
  state: TProfileState,
  action: TProfileAction
): Promise<TProfileState> => {
  try {
    // allProfiles were already previously loaded, update them too
    const hasAllProfiles = !!state?.allProfiles?.items.length;

    switch (action.type) {
      case GET_ALL_PROFILES: {
        // Removes type from variables
        const { type, ...variables } = action; // eslint-disable-line @typescript-eslint/no-unused-vars

        const newAllProfilesState = await graphQlClient(
          QUERY_GET_ALL_PROFILES,
          variables
        );

        // The GraphQL client can return null/undefined when the backend
        // responds with errors only (no data). Treat that as "no change"
        // instead of wiping the previously-loaded list out of state.
        if (!newAllProfilesState) {
          return state;
        }

        return { ...state, allProfiles: newAllProfilesState };
      }

      case GET_PROFILE: {
        const newProfileState = await graphQlClient(QUERY_GET_PROFILE, {
          id: action.id,
        });

        return { ...state, profile: newProfileState };
      }

      case UPDATE_PROFILE: {
        const {
          type, // eslint-disable-line @typescript-eslint/no-unused-vars
          payload,
          id,
        } = action;

        const newProfileState = await graphQlClient(MUTATION_UPDATE_PROFILE, {
          id,
          input: payload,
        });

        const newState = {
          ...state,
          profile: newProfileState,
        };

        if (hasAllProfiles) {
          const newAllProfilesItemsState = state?.allProfiles?.items?.map(
            (profile) =>
              profile.id === newProfileState.id ? newProfileState : profile
          );

          newState.allProfiles.items = newAllProfilesItemsState;
        }

        return newState;
      }

      case UPDATE_USER: {
        const newUserState = await graphQlClient(MUTATION_UPDATE_USER, {
          input: action.payload,
          sub: action.id,
        });

        const newProfileState = {
          ...state.profile,
          user: newUserState,
        } as Profile;

        const newState = { ...state, profile: newProfileState };

        if (hasAllProfiles) {
          const newAllProfilesItemsState = state?.allProfiles?.items?.map(
            (profile) =>
              profile.id === newProfileState.id ? newProfileState : profile
          );

          newState.allProfiles.items = newAllProfilesItemsState;
        }

        return newState;
      }

      // Both actions are almost identical
      case CANCEL_ABO: {
        const {
          type, // eslint-disable-line @typescript-eslint/no-unused-vars
          ...variables
        } = action;

        const newProfileState = await graphQlClient(
          MUTATION_CANCEL_ABO,
          variables
        );

        const newState = { ...state, profile: newProfileState };

        if (hasAllProfiles) {
          const newAllProfilesItemsState = state?.allProfiles?.items?.map(
            (profile) =>
              profile.id === newProfileState.id ? newProfileState : profile
          );

          newState.allProfiles.items = newAllProfilesItemsState;
        }

        return newState;
      }

      case REVOKE_ABO: {
        const {
          type, // eslint-disable-line @typescript-eslint/no-unused-vars
          ...variables
        } = action;

        const isRevoked = await graphQlClient(MUTATION_REVOKE_ABO, variables);

        if (isRevoked) {
          // Reload profile
          return await profileReducer(state, {
            type: GET_PROFILE,
            id: variables.profileId,
          });
        } else {
          notify(
            "Es war NICHT möglich, das Abonnement zu widerrufen. Bitte kontaktieren Sie die IT-Abteilung."
          );
          return state;
        }
      }

      case CHANGE_USER: {
        const {
          type, // eslint-disable-line @typescript-eslint/no-unused-vars
          oldUserId,
          newUserId,
          deleteOldUser = false,
        } = action;

        // Do modify current => current
        if (oldUserId === newUserId) {
          return state;
        }

        const input = {
          oldUserId,
          newUserId,
          deleteOldUser,
        };

        const newProfileState = await graphQlClient(
          MUTATION_CHANGE_USER,
          input
        );

        const newState = { ...state, profile: newProfileState };

        if (hasAllProfiles) {
          const newAllProfilesItemsState = state?.allProfiles?.items
            // Update new user
            ?.map((profile) =>
              profile.id === newUserId ? newProfileState : profile
            ) // Remove old user if required
            ?.filter(({ id }) => !deleteOldUser && id !== oldUserId);

          newState.allProfiles.items = newAllProfilesItemsState;

          newState.allProfiles.total = deleteOldUser
            ? state?.allProfiles?.total - 1
            : state?.allProfiles?.total;
        }

        return newState;
      }

      default: {
        return state;
      }
    }
  } catch (err) {
    handleCatchReducer("profile", err);
    // Keep the previous state so a single failed request does not wipe
    // the user list or the current profile out of the UI. The user gets
    // a notification via handleCatchReducer and can retry.
    return state;
  }
};
